import type {
  CreateSiteRequestDto,
  CreateZoneRequestDto,
  UpdateSiteRequestDto,
  UpdateZoneRequestDto,
} from '@evimo/client/models';
import { ResponseError } from '@evimo/client/runtime';
import type { PolygonPerimeter } from '@evimo/types';
import { z } from 'zod';
import {
  siteRequestInit,
  sitesApi,
  zonesApi,
} from '~/app/services/site/site-api-instances';
import { parseClientJsonResponse } from '~/app/services/http/parse-client-json-response';

type ApiErrorPayload = { message?: string | string[] };

async function throwSiteApiError(
  error: unknown,
  fallbackMessage: string,
): Promise<never> {
  if (error instanceof ResponseError) {
    const payload = (await error.response
      .json()
      .catch(() => null)) as ApiErrorPayload | null;
    const rawMessage = payload?.message;
    const message = Array.isArray(rawMessage)
      ? rawMessage.filter(Boolean).join('; ')
      : rawMessage;
    throw new Error(message || fallbackMessage);
  }
  throw error instanceof Error ? error : new Error(fallbackMessage);
}

const perimeterPointSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

const polygonPerimeterSchema = z
  .array(perimeterPointSchema)
  .min(3)
  .nullable()
  .optional()
  .transform((value) => value ?? null);

const paginationMetaSchema = z.object({
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  totalPages: z.number(),
});

const siteLocationSummarySchema = z.object({
  code: z.string(),
  name: z.string(),
});

const siteLookupSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: siteLocationSummarySchema.nullable(),
  province: siteLocationSummarySchema.nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  perimeter: polygonPerimeterSchema,
});

const provinceLookupSchema = z.object({
  code: z.string(),
  name: z.string(),
});

const cityLookupSchema = z.object({
  code: z.string(),
  name: z.string(),
  provinceCode: z.string(),
});

const zoneLookupSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  name: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  perimeter: polygonPerimeterSchema,
  isMain: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const siteAssetMapAssetSchema = z.object({
  id: z.string(),
  assetName: z.string(),
  assetTypeName: z.string(),
  assetModelName: z.string().nullable(),
  zoneName: z.string().nullable(),
  installedAt: z.string().nullable(),
  openIncidentsCount: z.number().int().nonnegative(),
});

const siteAssetsMapResponseSchema = z.object({
  site: siteLookupSchema,
  assets: z.array(siteAssetMapAssetSchema),
});

const zoneAssetsMapResponseSchema = z.object({
  site: siteLookupSchema,
  zone: zoneLookupSchema,
  assets: z.array(siteAssetMapAssetSchema),
});

const siteOperationsMapSummarySchema = z.object({
  siteCount: z.number().int().nonnegative(),
  zoneCount: z.number().int().nonnegative(),
  totalAssets: z.number().int().nonnegative(),
  healthyAssets: z.number().int().nonnegative(),
  warningAssets: z.number().int().nonnegative(),
  criticalAssets: z.number().int().nonnegative(),
  totalOpenIncidents: z.number().int().nonnegative(),
});

const siteOperationsMapSiteSchema = siteLookupSchema.extend({
  zonesCount: z.number().int().nonnegative(),
  totalAssets: z.number().int().nonnegative(),
  healthyAssets: z.number().int().nonnegative(),
  warningAssets: z.number().int().nonnegative(),
  criticalAssets: z.number().int().nonnegative(),
  totalOpenIncidents: z.number().int().nonnegative(),
});

const siteOperationsMapZoneSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  name: z.string(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  perimeter: polygonPerimeterSchema,
  isMain: z.boolean(),
  totalAssets: z.number().int().nonnegative(),
  healthyAssets: z.number().int().nonnegative(),
  warningAssets: z.number().int().nonnegative(),
  criticalAssets: z.number().int().nonnegative(),
  totalOpenIncidents: z.number().int().nonnegative(),
});

const siteOperationsMapResponseSchema = z.object({
  organizationId: z.string(),
  summary: siteOperationsMapSummarySchema,
  sites: z.array(siteOperationsMapSiteSchema),
  zones: z.array(siteOperationsMapZoneSchema),
});

const paginatedSiteLookupListSchema = z.object({
  data: z.array(siteLookupSchema),
  meta: paginationMetaSchema,
});
const paginatedProvinceLookupListSchema = z.object({
  data: z.array(provinceLookupSchema),
  meta: paginationMetaSchema,
});
const paginatedCityLookupListSchema = z.object({
  data: z.array(cityLookupSchema),
  meta: paginationMetaSchema,
});
const zoneLookupListSchema = z.array(zoneLookupSchema);

export type SiteLookup = z.infer<typeof siteLookupSchema>;
export type SiteLocationSummary = z.infer<typeof siteLocationSummarySchema>;
export type ProvinceLookup = z.infer<typeof provinceLookupSchema>;
export type CityLookup = z.infer<typeof cityLookupSchema>;
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;
export type PaginatedSiteLookupList = z.infer<
  typeof paginatedSiteLookupListSchema
>;
export type PaginatedProvinceLookupList = z.infer<
  typeof paginatedProvinceLookupListSchema
>;
export type PaginatedCityLookupList = z.infer<
  typeof paginatedCityLookupListSchema
>;
export type ZoneLookup = z.infer<typeof zoneLookupSchema>;
export type SiteAssetMapAsset = z.infer<typeof siteAssetMapAssetSchema>;
export type SiteAssetsMapResponse = z.infer<typeof siteAssetsMapResponseSchema>;
export type ZoneAssetsMapResponse = z.infer<typeof zoneAssetsMapResponseSchema>;
export type SiteOperationsMapSummary = z.infer<
  typeof siteOperationsMapSummarySchema
>;
export type SiteOperationsMapSite = z.infer<typeof siteOperationsMapSiteSchema>;
export type SiteOperationsMapZone = z.infer<typeof siteOperationsMapZoneSchema>;
export type SiteOperationsMapResponse = z.infer<
  typeof siteOperationsMapResponseSchema
>;
export type CreateSitePayload = {
  organizationId: string;
  name: string;
  address?: string | null;
  postalCode?: string | null;
  cityCode?: string | null;
  provinceCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  perimeter?: PolygonPerimeter | null;
};
export type UpdateSitePayload = {
  name?: string;
  address?: string | null;
  postalCode?: string | null;
  cityCode?: string | null;
  provinceCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  perimeter?: PolygonPerimeter | null;
};
export type ListQueryParams = {
  page: number;
  limit: number;
  q?: string;
  provinceCode?: string;
};
export type CreateZonePayload = {
  name: string;
  latitude?: number | null;
  longitude?: number | null;
  perimeter?: PolygonPerimeter | null;
  isMain?: boolean;
};
export type UpdateZonePayload = {
  siteId?: string;
  name?: string;
  latitude?: number | null;
  longitude?: number | null;
  perimeter?: PolygonPerimeter | null;
  isMain?: boolean;
};

export const SiteService = {
  async create(payload: CreateSitePayload): Promise<SiteLookup> {
    try {
      const apiResponse =
        await sitesApi.organizationSiteControllerCreateSiteRaw(
          {
            organizationId: payload.organizationId,
            createSiteRequestDto: payload as CreateSiteRequestDto,
          },
          siteRequestInit,
        );
      return parseClientJsonResponse(apiResponse, siteLookupSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to create site');
    }
  },

  async listByOrganizationId(
    organizationId: string,
    params: ListQueryParams,
  ): Promise<PaginatedSiteLookupList> {
    const normalizedOrganizationId = organizationId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    try {
      const apiResponse =
        await sitesApi.organizationSiteControllerFindSitesByOrganizationIdRaw(
          {
            organizationId: normalizedOrganizationId,
            page: params.page,
            limit: params.limit,
            ...(params.q?.trim() ? { q: params.q.trim() } : {}),
          },
          siteRequestInit,
        );
      return parseClientJsonResponse(
        apiResponse,
        paginatedSiteLookupListSchema,
      );
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch sites');
    }
  },

  async findAllByOrganizationId(organizationId: string): Promise<SiteLookup[]> {
    const result = await this.listByOrganizationId(organizationId, {
      page: 1,
      limit: 1000,
    });

    return result.data;
  },

  async findOperationsMapByOrganizationId(
    organizationId: string,
  ): Promise<SiteOperationsMapResponse> {
    const normalizedOrganizationId = organizationId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    try {
      const apiResponse =
        await sitesApi.organizationSiteControllerFindSiteOperationsMapByOrganizationIdRaw(
          { organizationId: normalizedOrganizationId },
          siteRequestInit,
        );
      return parseClientJsonResponse(
        apiResponse,
        siteOperationsMapResponseSchema,
      );
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch site operations map');
    }
  },

  async listProvinces(
    params: ListQueryParams,
  ): Promise<PaginatedProvinceLookupList> {
    try {
      const apiResponse = await sitesApi.siteControllerListProvincesRaw(
        {
          page: params.page,
          limit: params.limit,
          ...(params.q?.trim() ? { q: params.q.trim() } : {}),
        },
        siteRequestInit,
      );
      return parseClientJsonResponse(
        apiResponse,
        paginatedProvinceLookupListSchema,
      );
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch provinces');
    }
  },

  async listCities(params: ListQueryParams): Promise<PaginatedCityLookupList> {
    try {
      const apiResponse = await sitesApi.siteControllerListCitiesRaw(
        {
          page: params.page,
          limit: params.limit,
          ...(params.q?.trim() ? { q: params.q.trim() } : {}),
          ...(params.provinceCode?.trim()
            ? { provinceCode: params.provinceCode.trim() }
            : {}),
        },
        siteRequestInit,
      );
      return parseClientJsonResponse(
        apiResponse,
        paginatedCityLookupListSchema,
      );
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch cities');
    }
  },

  async findZonesBySiteId(
    organizationId: string,
    siteId: string,
  ): Promise<ZoneLookup[]> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    try {
      const apiResponse = await zonesApi.zoneControllerFindZonesBySiteIdRaw(
        {
          organizationId: normalizedOrganizationId,
          siteId: normalizedSiteId,
        },
        siteRequestInit,
      );
      return parseClientJsonResponse(apiResponse, zoneLookupListSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch zones');
    }
  },

  async findAssetsMapById(
    organizationId: string,
    siteId: string,
  ): Promise<SiteAssetsMapResponse> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    try {
      const apiResponse =
        await sitesApi.organizationSiteControllerFindSiteAssetsMapByIdRaw(
          {
            organizationId: normalizedOrganizationId,
            siteId: normalizedSiteId,
          },
          siteRequestInit,
        );
      return parseClientJsonResponse(apiResponse, siteAssetsMapResponseSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch site assets map');
    }
  },

  async findZoneAssetsMapById(
    organizationId: string,
    siteId: string,
    zoneId: string,
  ): Promise<ZoneAssetsMapResponse> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();
    const normalizedZoneId = zoneId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    if (!normalizedZoneId) {
      throw new Error('Zone id is required');
    }

    try {
      const apiResponse = await zonesApi.zoneControllerFindZoneAssetsMapByIdRaw(
        {
          organizationId: normalizedOrganizationId,
          siteId: normalizedSiteId,
          id: normalizedZoneId,
        },
        siteRequestInit,
      );
      return parseClientJsonResponse(apiResponse, zoneAssetsMapResponseSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to fetch zone assets map');
    }
  },

  async updateById(
    organizationId: string,
    siteId: string,
    payload: UpdateSitePayload,
  ): Promise<SiteLookup> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    try {
      const apiResponse =
        await sitesApi.organizationSiteControllerUpdateSiteRaw(
          {
            organizationId: normalizedOrganizationId,
            siteId: normalizedSiteId,
            updateSiteRequestDto: payload as UpdateSiteRequestDto,
          },
          siteRequestInit,
        );
      return parseClientJsonResponse(apiResponse, siteLookupSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to update site');
    }
  },

  async deleteById(organizationId: string, siteId: string): Promise<void> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    try {
      await sitesApi.organizationSiteControllerDeleteSite(
        {
          organizationId: normalizedOrganizationId,
          siteId: normalizedSiteId,
        },
        siteRequestInit,
      );
    } catch (error) {
      return throwSiteApiError(error, 'Failed to delete site');
    }
  },

  async createZone(
    organizationId: string,
    siteId: string,
    payload: CreateZonePayload,
  ): Promise<ZoneLookup> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    try {
      const apiResponse = await zonesApi.zoneControllerCreateZoneRaw(
        {
          organizationId: normalizedOrganizationId,
          siteId: normalizedSiteId,
          createZoneRequestDto: payload as CreateZoneRequestDto,
        },
        siteRequestInit,
      );
      return parseClientJsonResponse(apiResponse, zoneLookupSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to create zone');
    }
  },

  async updateZoneById(
    organizationId: string,
    siteId: string,
    zoneId: string,
    payload: UpdateZonePayload,
  ): Promise<ZoneLookup> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();
    const normalizedZoneId = zoneId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    if (!normalizedZoneId) {
      throw new Error('Zone id is required');
    }

    try {
      const apiResponse = await zonesApi.zoneControllerUpdateZoneRaw(
        {
          organizationId: normalizedOrganizationId,
          siteId: normalizedSiteId,
          id: normalizedZoneId,
          updateZoneRequestDto: payload as UpdateZoneRequestDto,
        },
        siteRequestInit,
      );
      return parseClientJsonResponse(apiResponse, zoneLookupSchema);
    } catch (error) {
      return throwSiteApiError(error, 'Failed to update zone');
    }
  },

  async deleteZoneById(
    organizationId: string,
    siteId: string,
    zoneId: string,
  ): Promise<void> {
    const normalizedOrganizationId = organizationId.trim();
    const normalizedSiteId = siteId.trim();
    const normalizedZoneId = zoneId.trim();

    if (!normalizedOrganizationId) {
      throw new Error('Organization id is required');
    }

    if (!normalizedSiteId) {
      throw new Error('Site id is required');
    }

    if (!normalizedZoneId) {
      throw new Error('Zone id is required');
    }

    try {
      await zonesApi.zoneControllerDeleteZone(
        {
          organizationId: normalizedOrganizationId,
          siteId: normalizedSiteId,
          id: normalizedZoneId,
        },
        siteRequestInit,
      );
    } catch (error) {
      return throwSiteApiError(error, 'Failed to delete zone');
    }
  },
};
